import {useEffect, useState, memo, useCallback} from 'react';

import {useDispatch} from 'react-redux';

import {BodyWrapper, LoginBody} from '~/shared/components/loginStyled';
import {getLocalizationService} from '~/shared/services/localisationService';
import VerificationForm from '~/shared/components/VerificationForm';
import {VerificationFormContextProvider} from '~/common/contextProviders/VerificationFormContextProvider';
import {clearUserErrors} from '~/shared/store/actions';
import {trackEvent} from '~/shared/services/analytics';

import {
  onForgotPassword,
  onSubmitPassword,
  onSubmitSignUpVerification,
  onSubmitVerification,
  onSignUpSubmit,
  toStepVerificationMode,
} from './utils/facebookLoginOrSignUpHelper';
import SignUpForm from './SignUpForm';
import {LoginProvider, AuthFlowStep, ViewModes, LOGIN_PROVIDER_ANALYTICS_MAP} from './consts';
import PasswordForm from './PasswordForm';
import LoginForm from './LoginForm';

const ConnectProviderTo10bis = props => {
  const {email} = props;
  return <LoginForm {...props} initialValues={{email}} />;
};

const ProviderLogin = ({
  authFlowStep,
  setAuthFlowStep,
  verificationDetails,
  emailEntry,
  onSubmit,
  facebookUserAccessToken,
  facebookUserId,
  googleUserId,
  googleCode,
  setResErrors,
  resErrors,
  dispatch,
  setEmailEntry,
  setVerificationDetails,
  fromCheckout,
  loginProvider,
  pageView,
}) => {
  const {t} = getLocalizationService();

  const headerText = t('a_verification_code_was_sent_to_your_registered_phone_or_email');

  const handleSubmitVerification = ({authenticationCode}) => {
    trackEvent('registrationOTP_submit', {onCheckout: fromCheckout, authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider]});
    onSubmitVerification({
      onSubmit,
      authenticationCode,
      email: emailEntry,
      facebookUserAccessToken,
      facebookUserId,
      googleUserId,
      googleCode,
      authenticationToken: verificationDetails.authenticationToken,
      fromCheckout,
    });
  };

  const handleReSendCode = () => {
    trackEvent('hasClickedSendCodeAgain', {onCheckout: fromCheckout, authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider]});
    toStepVerificationMode({
      email: emailEntry,
      setEmailEntry,
      setVerificationDetails,
      setAuthFlowStep,
      setResErrors,
    });
  };
    
  return (
    <BodyWrapper
      id="login_tab_controls"
      aria-labelledby="login_tab_button"
      role="tabpanel"
      pageView={pageView}
    >
      <LoginBody verificationMode={authFlowStep === AuthFlowStep.OTP}>
        {authFlowStep === AuthFlowStep.OTP && (
          <VerificationFormContextProvider>
            <VerificationForm
              verificationDetails={verificationDetails}
              onReSendCode={handleReSendCode}
              onSubmit={handleSubmitVerification}
              email={emailEntry}
              headerText={headerText}
              t={t}
              setResErrors={setResErrors}
              resErrors={resErrors}
            />
          </VerificationFormContextProvider>
        )}
        {authFlowStep === AuthFlowStep.PASSWORD && (
          <PasswordForm
            onBackClick={() => {
              setAuthFlowStep(AuthFlowStep.INITIAL);
            }}
            onSubmitLoginWithEmailAndPassword={async ({password}) =>
              onSubmitPassword({email: emailEntry, facebookUserAccessToken, facebookUserId, password, onSubmit})}
            emailEntry={emailEntry}
            onForgotPassword={() => onForgotPassword({email: emailEntry, dispatch})}
            t={t}
          />
        )}
        {authFlowStep === AuthFlowStep.INITIAL && (
          <>
            <ConnectProviderTo10bis
              t={t}
              resErrors={resErrors}
              setResErrors={setResErrors}
              email={emailEntry}
              onSubmitEmail={({email}) =>
                toStepVerificationMode({
                  email,
                  setAuthFlowStep,
                  setEmailEntry,
                  setVerificationDetails,
                  setResErrors,
                  t,
                })}
            />
          </>
        )}
      </LoginBody>
    </BodyWrapper>
  );
};

const ProviderSignUp = ({
  authFlowStep,
  setAuthFlowStep,
  verificationDetails,
  setVerificationDetails,
  signUpDetails,
  setSignUpDetails,
  setResErrors,
  closeModal,
  dispatch,
  resErrors,
  facebookUserAccessToken,
  facebookUserId,
  googleCode,
  openLoginScreen,
  emailEntry,
  fullNameEntry,
  isFixedEmail,
  fromCheckout,
  loginProvider,
  pageView,
  onSuccess,
}) => {
  const {t} = getLocalizationService();

  const handleSignUpSubmit = entries => {
    trackEvent('hasSubmittedRegister', {onCheckout: fromCheckout, authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider]});
    onSignUpSubmit({entries, setSignUpDetails, setAuthFlowStep, setResErrors});
  };

  const handleSubmitSignupVerification = ({authenticationCode}) => {
    trackEvent('registrationOTP_submit', {onCheckout: fromCheckout, authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider]});
    onSubmitSignUpVerification({
      authenticationCode,
      setResErrors,
      signUpDetails: {...signUpDetails, googleCode},
      closeModal,
      dispatch,
      analyticsDataProps: {
        onCheckout: fromCheckout,
        authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider],
      },
      pageView,
      setAuthFlowStep,
      onSuccess,
      fromCheckout,
    });
  };

  return (
    <BodyWrapper
      id="register_tab_controls"
      aria-labelledby="register_tab_button"
      role="tabpanel"
      pageView={pageView}
    >
      <LoginBody className="login-body" verificationMode={authFlowStep === AuthFlowStep.OTP}>
        {authFlowStep === AuthFlowStep.OTP ? (
          <VerificationFormContextProvider>
            <VerificationForm
              verificationDetails={verificationDetails}
              onReSendCode={() =>
              onSignUpSubmit({entries: signUpDetails, setSignUpDetails, setAuthFlowStep, setResErrors})}
              onSubmit={handleSubmitSignupVerification}
              email={signUpDetails?.email}
              headerText={t('a_verification_code_was_sent_to_your_registered_phone_or_email')}
              setResErrors={setResErrors}
              resErrors={resErrors}
              toUpdatePhoneNumberScreen={() => {
              dispatch(clearUserErrors());
              setResErrors(null);
              setAuthFlowStep(AuthFlowStep.INITIAL);
            }}
            />
          </VerificationFormContextProvider>
      ) : (
        <>
          <SignUpForm
            onSubmit={handleSignUpSubmit}
            sendActivationCodeError={resErrors}
            initialValues={{
              facebookUserAccessToken,
              facebookUserId,
              cellPhone: signUpDetails?.cellPhone,
              wantPromotion: false,
              email: emailEntry,
              fullName: fullNameEntry,
            }}
            existingEmailAction={email => openLoginScreen({
              email,
              setVerificationDetails,
            })}
            isFixedEmail={isFixedEmail}
          />
        </>
      )}
      </LoginBody>
    </BodyWrapper>
  );
};

const ProviderLoginOrSignUp = ({
  providerViewMode,
  facebookUserAccessToken,
  facebookUserId,
  googleCode,
  openLoginScreen,
  onSubmit,
  closeModal,
  pageView,
  email,
  fullName,
  loginProvider,
  authFlowStep,
  setAuthFlowStep,
  isRedirectedToStepRef,
  verificationDetails,
  setVerificationDetails,
  fromCheckout,
  onSuccess,
}) => {
  const [emailEntry, setEmailEntry] = useState(email || '');
  const [resErrors, setResErrors] = useState();
  const [signUpDetails, setSignUpDetails] = useState();
  const dispatch = useDispatch();

  const isFixedEmail = loginProvider === LoginProvider.GOOGLE;

  const clearSteps = useCallback(() => {
    setAuthFlowStep(AuthFlowStep.INITIAL);
  }, [setAuthFlowStep]);

  const isLoginMode = providerViewMode === ViewModes.SIGN_IN_MODE;

  useEffect(() => {
    if (isRedirectedToStepRef.current) {
      isRedirectedToStepRef.current = false;
      return;
    }

    setResErrors(null);
    clearSteps();
  }, [isLoginMode, clearSteps, isRedirectedToStepRef]);

  return (
    <>
      {isLoginMode ? (
        <ProviderLogin
          {...{
            verificationDetails,
            emailEntry,
            onSubmit,
            facebookUserAccessToken,
            facebookUserId,
            googleCode,
            setResErrors,
            resErrors,
            authFlowStep,
            setAuthFlowStep,
            dispatch,
            setEmailEntry,
            setVerificationDetails,
            pageView,
            loginProvider,
            fromCheckout,
          }}
        />
      ) : (
        <ProviderSignUp
          {...{
            authFlowStep,
            setAuthFlowStep,
            verificationDetails,
            setVerificationDetails,
            signUpDetails,
            setSignUpDetails,
            setResErrors,
            closeModal,
            dispatch,
            resErrors,
            facebookUserAccessToken,
            facebookUserId,
            googleCode,
            openLoginScreen,
            emailEntry,
            fullNameEntry: fullName,
            isFixedEmail,
            fromCheckout,
            loginProvider,
            pageView,
            onSuccess,
          }}
        />
      )}
    </>
  );
};

export default memo(ProviderLoginOrSignUp);
